import reject from 'lodash/reject';
import { BasketModel, BASKET_TRACKS, BASKET_IDENTITY } from './model';
import { action, IReactionDisposer, runInAction } from 'mobx';
import { StorageModel } from '../storage/model';
import { APIController } from '../api/controller';
import { EnvModel } from '../env/model';
import { UserModel } from '../user/model';
import { FlashController } from '../flash/controller';
import { OnboardingModel } from '../onboarding/model';
import { AnalyticsController } from '../analytics/controller';
import { ImageController } from '../image/controller';
import { FlashMessageTypeInfo } from '../../types';
import { getTrackImageURL } from '../../lib/image';
import { TrackSchema } from '../../types/schema';
import { APIError } from '../api/types';

export class BasketController {
  constructor(
    private model: BasketModel,
    private storage: StorageModel,
    private api: APIController,
    private env: EnvModel,
    private user: UserModel,
    private flash: FlashController,
    private onboarding: OnboardingModel,
    private analytics: AnalyticsController,
    private image: ImageController,
  ) {
    this._disposers = [];
  }

  private _disposers: IReactionDisposer[];

  dispose = () => {
    this._disposers.forEach((dispose) => dispose());
  };

  @action
  getBasket = async () => {
    if (!this.model.identity) return;

    runInAction(() => {
      this.model.sync = true;
    });

    try {
      const resp = await this.api.checkout.getBasket(this.model.identity);

      runInAction(() => {
        this.model.sync = false;
        this.storage.setItem(BASKET_IDENTITY, resp.data.identity);
        // TODO: Should we cater for locally stored tracks that aren't part of the response?
        this.storage.setItem(BASKET_TRACKS, resp.data.tracks);
      });
    } catch (err) {
      if (err instanceof APIError) {
        if (err.isServerError || err.isNetworkError) {
          // Server error, assume API issues and do nothing
          return;
        }
        // TODO: If we get a 404 and have a local basket, we should probably migrate our local basket over to a new basket?
      }
      // TODO: Throw up a modal to explain the issue to the user (rather than sudden empty basket)
      // Catastrophic failure, the basket is invalid in some way (maybe API 4xx or client error, nuclear option: dump state and create a valid basket
      this.reset();
      await this.maybeCreateBasket();
    }
  };

  @action
  private maybeCreateBasket = async () => {
    if (this.model.identity) return;

    runInAction(() => {
      this.model.sync = true;
    });

    const resp = await this.api.checkout.createBasket();

    runInAction(() => {
      this.model.sync = false;
      this.storage.setItem(BASKET_IDENTITY, resp.data.identity);

      if (resp.data.tracks.length > 0) {
        this.storage.setItem(BASKET_TRACKS, resp.data.tracks);
      } else {
        this.storage.setItem(BASKET_TRACKS, []);
      }
    });
  };

  @action
  addToBasket = async (track: TrackSchema, brandSponsored = false) => {
    const { tracks } = this.model;
    if (tracks.find((t) => t.identity === track.identity)) return;

    runInAction(() => {
      this.model.sync = true;
      this.model.syncItems.push(track);
    });

    await this.maybeCreateBasket();

    const resp = await this.api.checkout.addToBasket(this.model.identity, track.identity, 1, brandSponsored);

    runInAction(() => {
      this.model.sync = false;
      this.model.syncItems = reject(this.model.syncItems, (t) => t.identity === track.identity);

      if (!this.model.identity) {
        this.storage.setItem(BASKET_IDENTITY, resp.data.identity);
      }

      this.storage.setItem(BASKET_TRACKS, resp.data.tracks);
    });

    if (!this.user.user) {
      this.flash.createMessage(FlashMessageTypeInfo, {
        title: "You've just Lickd a track!",
        message: 'Sign in now to continue to checkout and get 25% off your first track',
        image: getTrackImageURL(track, this.image.preloadImage),
      });
    }

    this.analytics.sendAddToBasket(track);
    this.analytics.sendMixpanel('Added to basket', {
      identity: track.identity,
      track: track.title,
      slug: track.slug,
      artists: track.artists.map((artist) => {
        return artist.name;
      }),
    });
  };

  @action
  removeFromBasket = async (track: TrackSchema) => {
    runInAction(() => {
      this.model.sync = true;
      this.model.syncItems.push(track);
    });

    await this.maybeCreateBasket();

    const resp = await this.api.checkout.removeFromBasket(this.model.identity, track.identity);

    runInAction(() => {
      this.model.sync = false;
      this.model.syncItems = reject(this.model.syncItems, (t) => t.identity === track.identity);

      if (!this.model.identity) {
        this.storage.setItem(BASKET_IDENTITY, resp.data.identity);
      }

      this.storage.setItem(BASKET_TRACKS, resp.data.tracks);
    });

    this.analytics.sendRemoveFromBasket(track);
  };

  @action
  reset = () => {
    this.storage.delete(BASKET_IDENTITY);
    this.storage.delete(BASKET_TRACKS);
  };
}
